/**
 * Action Sidebar — action menu that lives inside the modal at top-right.
 * Trigger = one ◆ running a real freakout (rapid-fire glyph replacement, same mechanic as 404)
 * at a slow ambient pace: burst ~500ms, rest ~2-4s, repeat. Slow rotation wraps it.
 * Panel opens down-left with Home, Back, Glitch toggle.
 */
import { useState, useRef, useEffect } from "react"
import { Link } from "react-router-dom"
import { Home, ArrowLeft } from "lucide-react"
import { GLYPHS, COLOR_BASES } from "../lib/glitch"
import { navTargetToPath } from "../lib/routes"
import { GlitchSwitch } from "./GlitchSwitch"
import { useGlassTokens } from "../lib/useAppBackground"

const TRIGGER_CHAR = "◆"
const FREAK_COLS = COLOR_BASES.mixed

export interface ActionSidebarProps {
  showHome?: boolean
  showBack?: boolean
  showGlitch?: boolean
}

export function ActionSidebar({
  showHome = true,
  showBack = true,
  showGlitch = true,
}: ActionSidebarProps) {
  const glass = useGlassTokens()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const [displayChar, setDisplayChar] = useState(TRIGGER_CHAR)
  const [displayCol, setDisplayCol] = useState<string | undefined>(undefined)
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([])

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout)
    timersRef.current = []
  }

  useEffect(() => {
    let alive = true

    const scheduleRest = () => {
      if (!alive) return
      const rest = 2000 + Math.random() * 2000
      const t = setTimeout(() => { if (alive) runBurst() }, rest)
      timersRef.current.push(t)
    }

    const runBurst = () => {
      if (!alive) return
      const burstLen = 400 + Math.random() * 200
      const t0 = Date.now()
      const fire = () => {
        if (!alive) return
        if (Date.now() - t0 >= burstLen) {
          setDisplayChar(TRIGGER_CHAR)
          setDisplayCol(undefined)
          scheduleRest()
          return
        }
        setDisplayChar(GLYPHS[Math.floor(Math.random() * GLYPHS.length)])
        setDisplayCol(FREAK_COLS[Math.floor(Math.random() * FREAK_COLS.length)])
        timersRef.current.push(setTimeout(fire, 50 + Math.random() * 40))
      }
      fire()
    }

    timersRef.current.push(setTimeout(() => { if (alive) runBurst() }, 800 + Math.random() * 1200))
    return () => { alive = false; clearTimers() }
  }, [])

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener("click", handleClick, true)
    return () => document.removeEventListener("click", handleClick, true)
  }, [open])

  return (
    <div className="relative shrink-0" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Open menu"
        className="w-9 h-9 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 flex items-center justify-center transition-colors"
      >
        <span className="inline-flex items-center justify-center text-sm animate-[spin_20s_linear_infinite]" aria-hidden>
          <span className={displayCol ?? "text-white/70"}>{displayChar}</span>
        </span>
      </button>

      {open && (
        <div
          className={`absolute top-full right-0 mt-1.5 w-48 rounded-xl ${glass.modalBg} backdrop-blur-2xl border border-white/[0.08] shadow-[0_16px_48px_-12px_rgba(0,0,0,0.6)] overflow-hidden z-20 animate-in slide-in-from-top-2 fade-in duration-200 origin-top-right`}
          role="menu"
        >
          <div className="flex flex-col gap-0.5 p-1.5">
            {showHome && (
              <Link
                to={navTargetToPath("home")}
                role="menuitem"
                className="flex items-center gap-2 rounded-lg bg-white/5 border border-transparent px-2.5 py-2 hover:bg-white/10 hover:border-white/10 transition-colors text-white/80 hover:text-white"
                onClick={() => setOpen(false)}
              >
                <Home className="w-4 h-4 shrink-0" />
                <span className="text-[10px] font-bold uppercase tracking-widest">Home</span>
              </Link>
            )}
            {showBack && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false)
                  window.history.back()
                }}
                className="flex items-center gap-2 rounded-lg bg-white/5 border border-transparent px-2.5 py-2 hover:bg-white/10 hover:border-white/10 transition-colors text-white/80 hover:text-white text-left w-full"
              >
                <ArrowLeft className="w-4 h-4 shrink-0" />
                <span className="text-[10px] font-bold uppercase tracking-widest">Back</span>
              </button>
            )}
            {showGlitch && (
              <div className="pt-1.5 mt-1 border-t border-white/[0.06]" role="none">
                <GlitchSwitch variant="inline" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
